// Demo audio generator for focus music
export const createDemoAudioBlob = (type: 'calm' | 'nature' | 'focus' | 'ambient' = 'calm', duration: number = 30): string => {
  const sampleRate = 44100;
  const samples = duration * sampleRate;
  const buffer = new ArrayBuffer(44 + samples * 2);
  const view = new DataView(buffer);
  
  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples * 2, true);
  
  // Generate different types of audio based on type
  for (let i = 0; i < samples; i++) {
    let amplitude = 0;
    
    switch (type) {
      case 'calm':
        // Gentle sine wave with harmonics
        amplitude = 0.05 * (
          Math.sin(2 * Math.PI * 220 * i / sampleRate) +
          0.3 * Math.sin(2 * Math.PI * 440 * i / sampleRate) +
          0.1 * Math.sin(2 * Math.PI * 880 * i / sampleRate)
        );
        break;
      case 'nature':
        // White noise for nature sounds
        amplitude = 0.02 * (Math.random() * 2 - 1);
        break;
      case 'focus':
        // Binaural-like beats
        amplitude = 0.03 * (
          Math.sin(2 * Math.PI * 200 * i / sampleRate) +
          Math.sin(2 * Math.PI * 210 * i / sampleRate)
        );
        break;
      case 'ambient':
        // Multiple sine waves for ambient texture
        amplitude = 0.04 * (
          Math.sin(2 * Math.PI * 110 * i / sampleRate) +
          0.7 * Math.sin(2 * Math.PI * 165 * i / sampleRate) +
          0.4 * Math.sin(2 * Math.PI * 220 * i / sampleRate) +
          0.2 * Math.sin(2 * Math.PI * 330 * i / sampleRate)
        );
        break;
    }
    
    // Apply envelope for smooth start/end
    const fadeTime = sampleRate * 2; // 2 second fade
    if (i < fadeTime) {
      amplitude *= i / fadeTime;
    } else if (i > samples - fadeTime) {
      amplitude *= (samples - i) / fadeTime;
    }
    
    const sample = Math.max(-1, Math.min(1, amplitude));
    view.setInt16(44 + i * 2, sample * 0x7FFF, true);
  }
  
  const blob = new Blob([buffer], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
};

export const getDemoTrackUrl = (trackId: string): string => {
  switch (trackId) {
    case 'best':
    case 'lofi-beats':
      return createDemoAudioBlob('calm', 60);
    case 'classical-bach':
    case 'piano-solo':
      return createDemoAudioBlob('calm', 45);
    case 'nature-rain':
    case 'nature-forest':
    case 'ocean-waves':
      return createDemoAudioBlob('nature', 60);
    case 'binaural-alpha':
    case 'binaural-beta':
      return createDemoAudioBlob('focus', 30);
    case 'ambient-space':
      return createDemoAudioBlob('ambient', 50);
    default:
      return createDemoAudioBlob('calm', 30);
  }
};